/**
 * The start screen (contract C9): a large START button over the title card on every normal load. One click (or Enter /
 * Space) is the user gesture that turns sound on and starts the Director from frame 0. `nostart=1` skips it (tests).
 * Above START sits the race picker (YEAR then RACE selects, from the catalog). After START the SWITCH RACE button (top
 * left of the stage; RACES in the portrait header) pauses and brings the picker back at once over the paused frame,
 * with RESUME (r142: no reload). Only picking another race reloads: `GlCore.useBundle` is one-shot per load.
 */
import type { RaceDisplayInfo } from './races.ts';

export type PickerRace = RaceDisplayInfo & { round?: number | undefined };

/** The YEAR select's groups: season (newest first; no season sorts last as '?') -> its races by round. */
export function racesBySeason(races: readonly PickerRace[]): Map<string, PickerRace[]> {
  const sorted = [...races].sort((a, b) => (b.season ?? -1) - (a.season ?? -1) || (a.round ?? 0) - (b.round ?? 0));
  const out = new Map<string, PickerRace[]>();
  for (const r of sorted) {
    const y = r.season === undefined ? '?' : String(r.season);
    out.set(y, [...(out.get(y) ?? []), r]);
  }
  return out;
}

/** One RACE option: 'R17 · SINGAPORE GRAND PRIX'. */
export const raceOptionLabel = (r: PickerRace): string =>
  `${r.round !== undefined ? `R${String(r.round)} · ` : ''}${(r.name ?? r.id).toUpperCase()}`;
/** `switch`: the picker reopened after START (the player is paused under it) */
export type StartState = 'hidden' | 'shown' | 'started' | 'switch';

/** true when the URL asks to skip the screen (`nostart=1`, for tests only; not documented to users) */
export const skipStart = (search: string): boolean => new URLSearchParams(search).get('nostart') === '1';

/** hidden -> shown -> started; start() fires `onStart` once and only after show(). started <-> switch: reopen() and
 *  start() again (RESUME) fire `onSwitch(true / false)` */
export class StartMachine {
  state: StartState = 'hidden';

  constructor(private readonly onStart: () => void, private readonly onSwitch: (open: boolean) => void = () => undefined) {}

  /** SWITCH RACE: open the picker after START, or close it again (a toggle) */
  reopen(): void {
    if (this.state === 'started') {
      this.state = 'switch';
      this.onSwitch(true);
    } else if (this.state === 'switch') this.start();
  }

  show(): void {
    if (this.state === 'hidden') this.state = 'shown';
  }

  start(): boolean {
    if (this.state === 'switch') {
      this.state = 'started';
      this.onSwitch(false);
      return true;
    }
    if (this.state !== 'shown') return false;
    this.state = 'started';
    this.onStart();
    return true;
  }

  /** Enter or Space starts or resumes, Escape also resumes (true = the key was used and must not reach the shell);
   *  held-key repeats are used but do nothing */
  key(e: { key: string; repeat: boolean }): boolean {
    const resume = this.state === 'switch' && e.key === 'Escape';
    if (!resume && ((this.state !== 'shown' && this.state !== 'switch') || (e.key !== 'Enter' && e.key !== ' '))) return false;
    if (!e.repeat) this.start();
    return true;
  }
}

export class StartScreen {
  readonly el: HTMLDivElement;
  private readonly m: StartMachine;
  private readonly btn: HTMLButtonElement;
  private readonly switchBtn: HTMLButtonElement;
  private readonly note: HTMLDivElement;
  private canSwitch = false;

  /** `chromeRoot` holds the SWITCH RACE button so it fades with the transport chrome. `onSwitch(open)`: the picker
   *  reopened after START (pause) or closed with RESUME. */
  constructor(parent: HTMLElement, chromeRoot: HTMLElement, onStart: () => void, onSwitch: (open: boolean) => void = () => undefined) {
    this.m = new StartMachine(() => {
      this.el.hidden = true;
      this.switchBtn.hidden = !this.canSwitch;
      onStart();
    }, (open) => {
      this.el.hidden = !open;
      this.el.dataset.mode = open ? 'switch' : '';
      this.btn.textContent = open ? 'RESUME' : 'START';
      this.note.hidden = open;
      if (open) this.btn.focus({ preventScroll: true });
      onSwitch(open);
    });
    this.el = document.createElement('div');
    this.el.id = 'start';
    this.el.hidden = true;
    this.el.innerHTML = `<div class="race-picker" hidden>
        <label>YEAR <select id="pick-year"></select></label>
        <label>RACE <select id="pick-race"></select></label>
      </div>
      <button type="button" id="start-btn">START</button><div class="start-note">SOUND ON - CC ON - M MUTES</div>`;
    parent.appendChild(this.el);
    this.btn = this.el.querySelector('#start-btn') as HTMLButtonElement;
    this.note = this.el.querySelector('.start-note') as HTMLDivElement;
    this.btn.addEventListener('click', () => { this.m.start(); });
    this.switchBtn = document.createElement('button');
    this.switchBtn.type = 'button';
    this.switchBtn.id = 'switch-race';
    // portrait shows RACES in the header's left slot (r142); landscape SWITCH RACE
    this.switchBtn.setAttribute('aria-label', 'Switch race');
    this.switchBtn.title = 'Switch race';
    this.switchBtn.innerHTML = '<span class="label">SWITCH RACE</span><span class="portrait-only">RACES</span>';
    this.switchBtn.hidden = true;
    chromeRoot.appendChild(this.switchBtn);
    // capture phase: Space must start, not toggle play, while the screen is up (but not while a picker select has focus)
    document.addEventListener('keydown', (e) => {
      if (e.target instanceof HTMLSelectElement || !this.m.key(e)) return;
      e.preventDefault();
      e.stopPropagation();
    }, true);
  }

  get state(): StartState { return this.m.state; }

  /** The race picker: YEAR then RACE. Picking a race other than the current one calls `onPick` (a reload);
   *  SWITCH RACE reopens the picker in place (`StartMachine.reopen`). One race only (legacy path): no picker. */
  setRaces(races: readonly PickerRace[], currentId: string, onPick: (id: string) => void): void {
    const groups = racesBySeason(races);
    const year = this.el.querySelector('#pick-year') as HTMLSelectElement;
    const race = this.el.querySelector('#pick-race') as HTMLSelectElement;
    const curYear = [...groups].find(([, rs]) => rs.some((r) => r.id === currentId))?.[0] ?? '';
    const fillRaces = (): void => {
      race.replaceChildren(...(groups.get(year.value) ?? []).map((r) => new Option(raceOptionLabel(r), r.id, false, r.id === currentId)));
      // another year: a placeholder, so picking that year's only race still fires `change`
      if (year.value !== curYear) race.prepend(new Option('PICK A RACE', '', true, true));
    };
    year.replaceChildren(...[...groups.keys()].map((y) => new Option(y, y, false, y === curYear)));
    fillRaces();
    year.addEventListener('change', fillRaces);
    race.addEventListener('change', () => {
      if (race.value !== '' && race.value !== currentId) onPick(race.value);
    });
    this.switchBtn.onclick = () => { this.m.reopen(); };
    this.canSwitch = races.length > 1;
    (this.el.querySelector('.race-picker') as HTMLDivElement).hidden = !this.canSwitch;
    if (this.m.state === 'started') this.switchBtn.hidden = !this.canSwitch;
  }

  show(): void {
    this.m.show();
    this.el.hidden = this.m.state !== 'shown';
    this.btn.focus({ preventScroll: true });
  }
}
